import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, map, of, switchMap } from 'rxjs';
import { CompanyMetricSection, CompanyMetricSnapshot } from 'src/app/models/company-metric.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { CompanyMetricCardComponent, CompanyMetricCardViewModel } from './company-metric-card.component';

@Component({
  selector: 'app-company-metric',
  templateUrl: './company-metric.component.html',
  styleUrls: ['./company-metric.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CompanyMetricCardComponent],
})
export class CompanyMetricComponent {
  private readonly api = inject(ApiService);
  private readonly permissions = inject(PermissionsService);

  readonly company_product_id = input<number | null>(null);
  readonly date = input<Date>(new Date());
  readonly company_product_name = input('');
  readonly company_product_logo = input('');

  readonly refresh_requested = output<void>();

  private readonly refresh_tick = signal(0);

  private readonly request_params = computed(() => ({
    company_product_id: this.company_product_id(),
    date: this.date(),
    refresh_tick: this.refresh_tick(),
  }));

  readonly is_product_manager = computed(() => {
    const company_product_id = this.company_product_id();
    if (company_product_id === null) {
      return false;
    }

    return this.permissions.isBisualProductionTeamInCompanyProductWithRole(String(company_product_id), 'product-manager');
  });

  private readonly sections = toSignal(
    toObservable(this.request_params).pipe(
      switchMap(({ company_product_id, date }) => {
        if (!company_product_id) {
          return of([] as CompanyMetricSectionCard[]);
        }

        return this.api.getCompanyMetrics(company_product_id, date).pipe(
          map(response =>
            (response.company_metric_sections ?? [])
              .map(section => this.flattenSection(section))
              .sort((left_section, right_section): number => left_section.order - right_section.order)
          ),
          catchError(() => of([] as CompanyMetricSectionCard[]))
        );
      })
    ),
    { initialValue: [] as CompanyMetricSectionCard[] }
  );

  readonly levels = computed(() => {
    const sections = this.sections();
    const company_sections = sections.filter(section => section.relatable_type === 'company');
    const product_sections = sections.filter(section => section.relatable_type === 'company_product');

    const levels: CompanyMetricLevelGroup[] = [];
    if (product_sections.length > 0) {
      levels.push({ key: 'product', title: 'Product Level', sections: product_sections });
    }
    if (company_sections.length > 0) {
      levels.push({ key: 'company', title: 'Company Level', sections: company_sections });
    }

    return levels;
  });

  readonly has_metrics = computed(() => this.levels().length > 0);

  onMetricUpdated(): void {
    this.refresh_tick.update(tick => tick + 1);
    this.refresh_requested.emit();
  }

  private flattenSection(section: CompanyMetricSection): CompanyMetricSectionCard {
    const company_metrics = (section.company_metrics ?? [])
      .map(metric => this.flattenMetric(metric, section))
      .sort((left_metric, right_metric): number => {
        if (left_metric.visualization_order !== right_metric.visualization_order) {
          return left_metric.visualization_order - right_metric.visualization_order;
        }

        return left_metric.id - right_metric.id;
      });

    return {
      id: section.id,
      title: section.title,
      slug: section.slug,
      order: section.order,
      relatable_type: section.relatable_type,
      company_metrics,
    };
  }

  private flattenMetric(metric: CompanyMetricSnapshot, section: CompanyMetricSection): CompanyMetricCardViewModel {
    const charts = (metric.visualizations ?? [])
      .map(visualization => visualization.json)
      .filter((json): json is Record<string, unknown> => json != null && typeof json === 'object');

    const is_product_manager = this.is_product_manager();
    const can_edit_variable_condition =
      is_product_manager && metric.can_edit_variable_condition && metric.company_metric_variable_condition_id !== null;

    return {
      id: metric.id,
      title: metric.title,
      slug: metric.slug,
      value: metric.value,
      created_at: metric.created_at,
      visualization_order: metric.section_order,
      bootstrap_class: metric.bootstrap_class ?? 'col-12',
      charts,
      company_metric_relatable: section.relatable_type === 'company' ? 'company' : 'product',
      simple_type_value: metric.value_text ?? (metric.value != null ? String(metric.value) : ''),
      simple_type_value_units: metric.value_unit,
      simple_type_value_color: metric.value_color,
      simple_type_incremental_prcnt: metric.incremental_prcnt,
      simple_type_incremental_value: metric.incremental_value,
      simple_type_incremental_color: metric.incremental_color,
      simple_type_footer_text: metric.footer_text,
      simple_type_suffix_type: metric.suffix_type ?? 'none',
      simple_type_suffix_icon_google_name: metric.suffix_icon_google_name,
      simple_type_suffix_progress_bar_prcnt: metric.suffix_progress_bar_prcnt,
      simple_type_suffix_progress_bar_color: metric.suffix_progress_bar_color,
      simple_type_suffix_circular_progress_prcnt: metric.suffix_circular_progress_prcnt,
      simple_type_suffix_circular_progress_color: metric.suffix_circular_progress_color,
      simple_type_suffix_svg_line_html: metric.suffix_svg_line_html,
      company_metric_variable_condition_id: metric.company_metric_variable_condition_id,
      acceptable_value: metric.acceptable_value,
      good_value: metric.good_value,
      evaluation: metric.evaluation,
      unit: metric.unit,
      can_edit_variable_condition,
      has_month_trend: metric.has_month_trend,
      month_trend_percent: metric.month_trend_percent,
      month_trend_semantic: metric.month_trend_semantic,
      status_label: metric.status_label,
    };
  }
}

interface CompanyMetricSectionCard {
  id: number;
  title: string;
  slug: string;
  order: number;
  relatable_type: 'company' | 'company_product';
  company_metrics: CompanyMetricCardViewModel[];
}

interface CompanyMetricLevelGroup {
  key: 'company' | 'product';
  title: string;
  sections: CompanyMetricSectionCard[];
}
